import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import artistsSlice from "./features/artists/artistsSlice";
import authSlice from "./features/auth/authSlice";
import clubSlice from "./features/club/clubSlice";
import categorySlice from "./features/category/categorySlice";
import eventSlice from "./features/event/eventSlice";
import genresSlice from "./features/genres/genresSlice";
import languageSlice from "./features/language/languageSlice";
import notificationSlice from "./features/notification/notificationSlice";
import organiserSlice from "./features/organiser/organiserSlice";
import toastSlice from "./features/toast/toastSlice";
import userSlice from "./features/user/userSlice";

/**
 * The configuration object for persisting data using redux-persist.
 * @type {Object}
 * @property {string} key - The key to use for storing the data.
 * @property {Object} storage - The storage engine to use for persisting the data.
 */
const persistConfig = {
  key: "root",
  storage,
};

/**
 * Creates a persisted reducer using the given persist configuration and slice.
 * @param {PersistConfig} persistConfig - The configuration object for persisting the state.
 * @param {Reducer} slice - The reducer slice to persist.
 * @returns The persisted reducer.
 */
const persistedReducer = persistReducer(persistConfig, authSlice);
const persistedUserReducer = persistReducer(persistConfig, userSlice);
const persistedCategoryReducer = persistReducer(persistConfig, categorySlice);
const persistedGenresReducer = persistReducer(persistConfig, genresSlice);
const persistedartistsReducer = persistReducer(persistConfig, artistsSlice);
const persistedEventReducer = persistReducer(persistConfig, eventSlice);
const persistedLanguageReducer = persistReducer(persistConfig, languageSlice);
const persistedOrganiserReducer = persistReducer(persistConfig, organiserSlice);
const persistedNotificationReducer = persistReducer(
  persistConfig,
  notificationSlice
);

/**
 * Combines multiple reducers into a single root reducer.
 * @param {object} reducers - An object containing the reducers to be combined.
 * @returns The combined root reducer.
 */
const rootReducer = combineReducers({
  toast: toastSlice,
  club: clubSlice,
  auth: persistedReducer,
  users: persistedUserReducer,
  category: persistedCategoryReducer,
  genres: persistedGenresReducer,
  artist: persistedartistsReducer,
  event: persistedEventReducer,
  language: persistedLanguageReducer,
  organiser: persistedOrganiserReducer,
  notification: persistedNotificationReducer,
});

/**
 * Creates a Redux store with the specified reducers.
 * @param {Object} reducer - The root reducer object.
 * @param {Object} auth - The persisted reducer for authentication state.
 * @param {Object} events - The persisted reducer for events state.
 * @returns The configured Redux store.
 */
export const store = configureStore({
  reducer: rootReducer,
  devTools: true,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
        ],
      },
    }),
});

export const persistor = persistStore(store);
